import React, { useState } from 'react';
import { Send, Plus, Trash2, MessageSquare } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { api } from '../../utils/api';

const VAR_OPTIONS = [
  { value: 'studentName', label: 'Student Name' },
  { value: 'fatherName',  label: 'Father Name' },
  { value: 'motherName',  label: 'Mother Name' },
  { value: 'className',   label: 'Class' },
  { value: 'staffName',   label: 'Staff Name' },
  { value: 'schoolName',  label: 'School Name' },
  { value: 'custom',      label: 'Custom Text' },
];

const SAMPLE = {
  studentName: 'Arjun Kumar',
  fatherName:  'Rajesh Kumar',
  motherName:  'Priya Devi',
  className:   '5-A',
  staffName:   'Vijay Sharma',
  schoolName:  'Vasavya Vidyalayam',
};

const ROLE_OPTIONS = [
  { value: 'father', label: 'Father', desc: "Sends to father's WhatsApp number" },
  { value: 'mother', label: 'Mother', desc: "Sends to mother's WhatsApp number" },
  { value: 'staff',  label: 'Staff',  desc: "Sends to each staff member's number" },
];

function previewParam(p) {
  if (!p) return '';
  if (p.type === 'custom') return p.value || '(empty)';
  return SAMPLE[p.type] || '';
}

function WhatsAppMessages() {
  const { students, staff } = useApp();

  const [templateName, setTemplateName] = useState('');
  const [language,     setLanguage]     = useState('en');
  const [params,       setParams]       = useState([{ type: 'studentName', value: '' }]);
  const [roles,        setRoles]        = useState(new Set(['father']));
  const [sending,      setSending]      = useState(false);
  const [toast,        setToast]        = useState(null);

  const addParam    = () => setParams(p => [...p, { type: 'studentName', value: '' }]);
  const removeParam = (i) => setParams(p => p.filter((_, idx) => idx !== i));
  const updateParam = (i, field, val) => setParams(p => p.map((x, idx) => idx === i ? { ...x, [field]: val } : x));
  const toggleRole  = (r) => setRoles(prev => { const s = new Set(prev); s.has(r) ? s.delete(r) : s.add(r); return s; });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Estimate recipient count
  const recipientCount = (() => {
    const r = [...roles];
    let n = 0;
    if (r.includes('father') || r.includes('mother')) {
      const active = students.filter(s => s.status === 'active');
      if (r.includes('father')) n += active.filter(s => s.fatherPhone).length;
      if (r.includes('mother')) n += active.filter(s => s.motherPhone).length;
    }
    if (r.includes('staff')) n += staff.filter(s => s.status === 'active' && s.phone).length;
    return n;
  })();

  const handleSend = async () => {
    if (!templateName.trim()) { showToast('Enter a template name', 'error'); return; }
    if (!roles.size)           { showToast('Select at least one target role', 'error'); return; }
    if (!params.length)        { showToast('Add at least one parameter', 'error'); return; }

    const confirmed = window.confirm(
      `Send WhatsApp template "${templateName}" to ~${recipientCount} recipients?\n\nThis will use your Way2Chats credits.`
    );
    if (!confirmed) return;

    setSending(true);
    try {
      await api.post('/whatsapp/send-bulk', {
        templateName: templateName.trim(),
        language:     language.trim() || 'en',
        params,
        targetRoles:  [...roles],
      });
      showToast(`Sending to ~${recipientCount} recipients in background.`);
    } catch (e) {
      showToast(e.response?.data?.message || 'Send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-100 rounded-xl"><MessageSquare size={22} className="text-green-600" /></div>
        <div>
          <h1 className="text-xl font-bold">WhatsApp API Messages</h1>
          <p className="text-sm text-gray-500">Send bulk WhatsApp template messages via Way2Chats</p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
        }`}>{toast.msg}</div>
      )}

      {/* Template */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-gray-700 mb-3">Template</h2>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Template Name</label>
            <input
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder="e.g. schoolnotice"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs text-gray-500 mb-1">Language</label>
            <input
              value={language}
              onChange={e => setLanguage(e.target.value)}
              placeholder="en"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            />
          </div>
        </div>
      </div>

      {/* Parameters */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-700">Parameters</h2>
          <button onClick={addParam}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100">
            <Plus size={13} /> Add
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-3">Map each &#123;&#123;N&#125;&#125; placeholder in your template to a field.</p>
        <div className="space-y-2">
          {params.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-10 text-center text-xs font-bold text-gray-500 bg-gray-100 rounded py-1.5">
                &#123;&#123;{i + 1}&#125;&#125;
              </span>
              <select
                value={p.type}
                onChange={e => updateParam(i, 'type', e.target.value)}
                className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              >
                {VAR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {p.type === 'custom' && (
                <input
                  value={p.value}
                  onChange={e => updateParam(i, 'value', e.target.value)}
                  placeholder="Text..."
                  className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                />
              )}
              <span className="text-xs text-gray-400 min-w-[80px] truncate italic">→ {previewParam(p)}</span>
              <button onClick={() => removeParam(i)} disabled={params.length === 1}
                className="p-1 text-gray-300 hover:text-red-500 disabled:opacity-30">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Target Roles */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-gray-700 mb-3">Target Audience</h2>
        <div className="space-y-2">
          {ROLE_OPTIONS.map(r => (
            <label key={r.value}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                roles.has(r.value)
                  ? 'bg-green-50 border-green-400 text-green-800'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}>
              <input type="checkbox" className="hidden"
                checked={roles.has(r.value)}
                onChange={() => toggleRole(r.value)} />
              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                roles.has(r.value) ? 'border-green-500 bg-green-500' : 'border-gray-300'
              }`}>
                {roles.has(r.value) && <span className="text-white text-xs font-bold">✓</span>}
              </span>
              <div>
                <span className="font-medium">{r.label}</span>
                <span className="text-xs ml-2 text-gray-400">{r.desc}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
        <p className="text-xs font-semibold text-green-700 mb-1">Preview (sample data)</p>
        <p className="text-xs text-gray-600">
          Template: <span className="font-mono font-bold">{templateName || '(template name)'}</span>
          &nbsp;|&nbsp;Language: <span className="font-mono font-bold">{language || 'en'}</span>
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Params: {params.map((p, i) => (
            <span key={i}><span className="font-mono text-green-700">&#123;&#123;{i+1}&#125;&#125;</span>=<span className="font-semibold">{previewParam(p)}</span>{i < params.length - 1 ? ', ' : ''}</span>
          ))}
        </p>
        <p className="text-xs text-gray-500 mt-1.5">
          Estimated recipients: <span className="font-bold text-green-700">{recipientCount}</span>
          {[...roles].map(r => (
            <span key={r} className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs capitalize">{r}</span>
          ))}
        </p>
      </div>

      {/* Send */}
      <button
        onClick={handleSend}
        disabled={sending || !templateName.trim() || !roles.size}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Send size={16} />
        {sending ? 'Sending...' : `Send to ~${recipientCount} Recipients`}
      </button>
    </div>
  );
}

export default WhatsAppMessages;
